/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Do these need to be imported for the typing to work correctly?
import {
  deepOrange,
  green,
  grey,
  lightBlue,
  orange,
  red,
  yellow,
} from '@mui/material/colors';
import type {
  CommonColors,
  PaletteOptions,
  Palette,
  PaletteColor,
  PaletteColorOptions,
  TypeBackground,
} from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface PaletteGradientColor extends PaletteColor {
    gradient: string;
    iconGradient: string;
  }

  interface TypeStatus {
    critical: string;
    high: string;
    medium: string;
    unassigned: string;
    low: string;
    none: string;
    new: string;
  }

  interface CommonColors {
    darkText: string;
    darkIcon: string;
    lightText: string;
    lightIcon: string;
  }

  interface TypeBackground {
    base: string;
  }

  interface AuroraColors {
    sea: string;
    midnight: string;
    pitch: string;
    mist: string;
    azure: string;
    aurora: string;
    sunset: string;
    bloodMood: string;
    steel: string;
  }

  interface SpectrumColors {
    darkBlue: string;
    mediumBlue: string;
    darkPink: string;
    mediumPink: string;
    darkPurple: string;
    mediumPurple: string;
  }

  interface MonochromeColors {
    mono1: string;
    mono2: string;
    mono3: string;
    mono4: string;
    mono5: string;
    mono6: string;
  }

  interface TemperatureColors {
    tempRed1: string;
    tempRed2: string;
    tempRed3: string;
    tempOrange1: string;
    tempOrange2: string;
    tempOrange3: string;
    tempYellow1: string;
    tempYellow2: string;
    tempYellow3: string;
    tempGreen1: string;
    tempGreen2: string;
    tempGreen3: string;
    tempBlue1: string;
    tempBlue2: string;
    tempBlue3: string;
    tempGrey1: string;
    tempGrey2: string;
    tempGrey3: string;
  }

  interface WidgetColors {
    borders: string;
    placeholder: string;
    chartBorders: string;
    widgetBackground: string;
    widgetHeaderBackground: string;
    dataSummaryPrimary: string;
    dataSummarySecondary: string;
    unfilled: string;
  }

  interface Palette {
    arcticAzure: Palette['grey']; // hacky way of using Color type
    arcticSunset: Palette['grey'];
    arcticBlue: Palette['grey'];
    arcticMidnight: Palette['grey'];
    arcticGrey: Palette['grey'];
    arcticAurora: AuroraColors;
    background: TypeBackground;
    border: PaletteColor;
    common: CommonColors;
    default: PaletteColor;
    monochrome: MonochromeColors;
    promo: PaletteGradientColor;
    spectrum: SpectrumColors;
    status: TypeStatus;
    temperature: TemperatureColors;
    widget: WidgetColors;
  }

  interface PaletteOptions {
    arcticAzure?: PaletteOptions['grey']; // hacky way of using ColorPartial type
    arcticSunset?: PaletteOptions['grey'];
    arcticBlue?: PaletteOptions['grey'];
    arcticMidnight?: PaletteOptions['grey'];
    arcticGrey?: PaletteOptions['grey'];
    arcticAurora?: Partial<AuroraColors>;
    background?: Partial<TypeBackground>;
    border?: PaletteColorOptions;
    common?: Partial<CommonColors>;
    default?: PaletteColorOptions;
    monochrome?: Partial<MonochromeColors>;
    promo?: Partial<PaletteGradientColor>;
    spectrum?: Partial<SpectrumColors>;
    status?: Partial<TypeStatus>;
    temperature?: Partial<TemperatureColors>;
    widget?: Partial<WidgetColors>;
  }
}

const contrastThreshold = 4.5;

const arcticBlue: Palette['arcticBlue'] = {
  50: '#e5ecf1',
  100: '#bdd0dd',
  200: '#92b1c7',
  300: '#6691b0',
  400: '#457a9f',
  500: '#24628e',
  600: '#205a86',
  700: '#1b507b',
  800: '#164671',
  900: '#0d345f',
  A100: '#94c2ff',
  A200: '#61a5ff',
  A400: '#2e88ff',
  A700: '#147aff',
};

const arcticSunset: Palette['arcticSunset'] = {
  50: '#fef0e4',
  100: '#fcdabc',
  200: '#fac190',
  300: '#f8a863',
  400: '#f79541',
  500: '#f58220',
  600: '#f47a1c',
  700: '#f26f18',
  800: '#f06513',
  900: '#ee520b',
  A100: '#ffffff',
  A200: '#ffece5',
  A400: '#ffc6b2',
  A700: '#ffb498',
};

const arcticAzure: Palette['arcticAzure'] = {
  50: '#e4f2fb',
  100: '#bde0f5',
  200: '#91cbee',
  300: '#64b6e7',
  400: '#43a6e2',
  500: '#2296dd',
  600: '#1e8ed9',
  700: '#1983d4',
  800: '#1479cf',
  900: '#0c68c7',
  A100: '#f2f8ff',
  A200: '#bfdcff',
  A400: '#8cc0ff',
  A700: '#73b2ff',
};

const arcticMidnight: Palette['arcticMidnight'] = {
  50: '#e0e6ea',
  100: '#b3c1cb',
  200: '#8098a8',
  300: '#4d6e85',
  400: '#264f6b',
  500: '#003051',
  600: '#002b4a',
  700: '#002440',
  800: '#001e37',
  900: '#001327',
  A100: '#629aff',
  A200: '#2f7aff',
  A400: '#005afb',
  A700: '#0051e1',
};

const arcticGrey: Palette['arcticGrey'] = {
  50: '#fff',
  100: '#f1f1f1',
  200: '#e4e4e4',
  300: '#dedede',
  400: '#d1d1d1',
  500: '#2e2e2e',
  600: '#212121',
  700: '#1b1b1b',
  800: '#161616',
  900: '#000',
  A100: '#ececec',
  A200: '#313131',
  A400: '#d1d1d1',
  A700: '#828282',
};

const arcticAurora: Palette['arcticAurora'] = {
  sea: '#0059B2',
  midnight: '#00244C',
  pitch: '#000000',
  mist: '#83D6FF',
  azure: '#0099FF',
  aurora: '#0066FF',
  sunset: '#FF730F',
  bloodMood: '#F83300',
  steel: '#182A3A',
};

// these colors don't change between themes
const common = {
  darkText: alpha('#000', 0.8),
  darkIcon: alpha('#000', 0.6),
  lightText: '#fff',
  lightIcon: alpha('#fff', 0.6),
} satisfies PaletteOptions['common']; // only include new color variables

const promoGradientStart = arcticAurora.sunset;
const promoGradientEnd = arcticAurora.bloodMood;

export const lightPalette = {
  contrastThreshold,
  action: {
    active: alpha('#000', 0.56),
    hover: alpha('#000', 0.04),
    selected: alpha('#000', 0.08),
    focus: alpha('#000', 0.12),
    disabled: alpha('#000', 0.38),
    disabledBackground: alpha('#000', 0.12),
  },
  background: {
    base: arcticGrey.A100,
  },
  text: {
    primary: alpha('#000', 0.87),
    secondary: alpha('#000', 0.6),
    disabled: alpha('#000', 0.38),
  },
  primary: {
    main: arcticAurora.sea,
    dark: arcticAurora.midnight,
    light: arcticAurora.azure,
    contrastText: '#fff',
  },
  secondary: {
    main: arcticAurora.sunset,
    dark: arcticAurora.bloodMood,
    light: arcticSunset[300],
    contrastText: '#fff',
  },
  error: {
    main: red[700],
    dark: red[800],
    light: red[400],
    contrastText: '#fff',
  },
  warning: {
    main: orange[800],
    dark: orange[900],
    light: orange[500],
    contrastText: '#fff',
  },
  info: {
    main: arcticAzure[700],
    dark: arcticAzure[900],
    light: arcticAzure[500],
    contrastText: '#fff',
  },
  success: {
    main: green[800],
    dark: green[900],
    light: green[500],
    contrastText: '#fff',
  },
  // custom palettes
  promo: {
    dark: promoGradientEnd,
    main: promoGradientStart,
    light: arcticSunset[300],
    gradient: `linear-gradient(90deg, ${promoGradientStart} 0%, ${promoGradientEnd} 100%)`,
    iconGradient: `linear-gradient(278deg, ${promoGradientStart} 0%, ${promoGradientEnd} 100%)`,
    contrastText: '#fff',
  },
  monochrome: {
    mono1: '#002556',
    mono2: '#29679f',
    mono3: '#4697d3',
    mono4: '#60b8f1',
    mono5: '#77cbf7',
    mono6: '#96e4ff',
  },
  spectrum: {
    darkBlue: '#0072b1',
    mediumBlue: '#54aeed',
    darkPink: '#a4357c',
    mediumPink: '#d25da5',
    darkPurple: '#712f9c',
    mediumPurple: '#8f88ce',
  },
  status: {
    critical: red[200],
    high: arcticSunset[200],
    medium: yellow[200],
    unassigned: grey[200],
    low: green[200],
    none: lightBlue[200],
    new: lightBlue[200],
  },
  temperature: {
    tempRed1: red[200],
    tempRed2: red[400],
    tempRed3: red[600],
    tempOrange1: arcticSunset[200],
    tempOrange2: arcticSunset[400],
    tempOrange3: arcticSunset[600],
    tempYellow1: yellow[200],
    tempYellow2: yellow[400],
    tempYellow3: yellow[600],
    tempGreen1: green[200],
    tempGreen2: green[400],
    tempGreen3: green[600],
    tempBlue1: lightBlue[200],
    tempBlue2: lightBlue[400],
    tempBlue3: lightBlue[600],
    tempGrey1: grey[200],
    tempGrey2: grey[400],
    tempGrey3: grey[600],
  },
  widget: {
    borders: grey[400],
    placeholder: grey[400],
    chartBorders: '#fff',
    dataSummaryPrimary: arcticAzure[800],
    dataSummarySecondary: arcticAurora.sunset,
    unfilled: '#ebebeb',
    widgetBackground: grey[50],
    widgetHeaderBackground: '#fff',
  },
  common,
  arcticBlue,
  arcticSunset,
  arcticAzure,
  arcticMidnight,
  arcticGrey,
  arcticAurora,
  // component palettes
  Alert: {
    // special case for info alert to use a darker background color so the text color can default to white in light mode
    infoFilledColor: '#fff',
    infoFilledBg: arcticAzure[900],
  },
  Tooltip: {
    // override background with opacity
    bg: grey[700],
  },
};

export const darkPalette = {
  contrastThreshold,
  action: {
    active: alpha('#fff', 0.56),
    hover: alpha('#fff', 0.08),
    selected: alpha('#fff', 0.16),
    focus: alpha('#fff', 0.12),
    disabled: alpha('#fff', 0.38),
    disabledBackground: alpha('#fff', 0.12),
  },
  background: {
    base: arcticGrey.A200,
  },
  text: {
    primary: '#fff',
    secondary: alpha('#fff', 0.7),
    disabled: alpha('#fff', 0.38),
  },
  primary: {
    main: '#3BB1FF', // lighter version of arcticAurora.azure
    dark: arcticAurora.sea,
    light: arcticAurora.mist,
    contrastText: alpha('#000', 0.87),
  },
  secondary: {
    main: '#FF9F5A',
    dark: '#FF643C',
    light: arcticSunset[100],
    contrastText: alpha('#000', 0.87),
  },
  error: {
    main: red[500],
    dark: red[700],
    light: red[300],
    contrastText: '#fff',
  },
  warning: {
    main: orange[400],
    dark: orange[700],
    light: orange[300],
    contrastText: alpha('#000', 0.87),
  },
  info: {
    main: arcticAzure[400],
    dark: arcticAzure[700],
    light: arcticAzure[300],
    contrastText: alpha('#000', 0.87),
  },
  success: {
    main: green[400],
    dark: green[700],
    light: green[300],
    contrastText: alpha('#000', 0.87),
  },
  // custom palettes
  promo: {
    dark: promoGradientEnd,
    main: promoGradientStart,
    light: arcticSunset[300],
    gradient: `linear-gradient(90deg, ${promoGradientStart} 0%, ${promoGradientEnd} 100%)`,
    iconGradient: `linear-gradient(278deg, ${promoGradientStart} 37.03%, ${promoGradientEnd} 61.43%)`,
    contrastText: alpha('#000', 0.87),
  },
  monochrome: {
    mono1: '#005284',
    mono2: '#2178ad',
    mono3: '#419dd6',
    mono4: '#63bfee',
    mono5: '#88def6',
    mono6: '#acfcfe',
  },
  spectrum: {
    darkBlue: '#005284',
    mediumBlue: '#50afe9',
    darkPink: '#a4357c',
    mediumPink: '#d25da5',
    darkPurple: '#712f9c',
    mediumPurple: '#8f88ce',
  },
  status: {
    critical: red[200],
    high: arcticSunset[200],
    medium: yellow[200],
    unassigned: grey[200],
    low: green[200],
    none: lightBlue[200],
    new: lightBlue[200],
  },
  temperature: {
    tempRed1: red[200],
    tempRed2: red[400],
    tempRed3: red[600],
    tempOrange1: deepOrange[200],
    tempOrange2: deepOrange[400],
    tempOrange3: deepOrange[600],
    tempYellow1: yellow[200],
    tempYellow2: yellow[400],
    tempYellow3: yellow[600],
    tempGreen1: green[200],
    tempGreen2: green[400],
    tempGreen3: green[600],
    tempBlue1: lightBlue[200],
    tempBlue2: lightBlue[400],
    tempBlue3: lightBlue[600],
    tempGrey1: grey[200],
    tempGrey2: grey[400],
    tempGrey3: grey[600],
  },
  widget: {
    borders: grey[600],
    placeholder: grey[600],
    chartBorders: '#121212',
    dataSummaryPrimary: arcticAzure[300],
    dataSummarySecondary: arcticAurora.sunset,
    unfilled: '#ebebeb',
    widgetBackground: arcticGrey[600],
    widgetHeaderBackground: '#121212',
  },
  common,
  arcticBlue,
  arcticSunset,
  arcticAzure,
  arcticMidnight,
  arcticGrey,
  arcticAurora,
  // component palettes
  Alert: {
    // include lighter backgrounds for standard variant in dark mode
    successStandardBg: '#003307',
    infoStandardBg: '#002e41',
    warningStandardBg: '#442900',
    errorStandardBg: '#520000',
  },
  Tooltip: {
    // override background with opacity
    bg: grey[700],
  },
};
